import { useMutation } from '@apollo/client/react';
import { Alert, Avatar, Box, Button, Field, Heading, Input, Link, NativeSelect, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';
import { UploadButton } from '@src/components/sco/Account/UploadButton';
import { DEFAULT_COVER_PHOTO_URL } from '@src/components/sco/Account/UserCoverPhotoSelector';
import { UserCoverPhotoSelectorModal } from '@src/components/sco/Account/UserCoverPhotoSelector/UserCoverPhotoSelectorModal';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers';
import { FormContainer } from '@src/components/sco/FormContainer';
import { LocationInput, LocationInputPlace } from '@src/components/sco/LocationInput';
import { CheckCircleIcon } from '@src/components/sco/icons';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { profileVisibilities } from '@src/constants/profile-visibilities';
import { UpdateProfileInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { scoBackendUrl, startupsUrl } from '@src/lib/startups-url';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuImage } from 'react-icons/lu';
type UserProfileFormFields = {
  bio?: string;
  coverPhotoUrl?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  pictureUrl?: string;
  shortBio?: string;
  title?: string;
  profileVisibilityKey?: string;
};
type UserProfileFormProps = GenericFormProps<UserProfileFormFields> & {
  submitControlled?: boolean;
  submitTrigger?: number;
  title?: string;
  showAdditionalFields?: boolean;
};
export const UserProfileForm = (props: UserProfileFormProps) => {
  const [formErrorMessage, setFormErrorMessage] = useState<string>();
  const [formSuccessMessage, setFormSuccessMessage] = useState<string>();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [pictureUrl, setPictureUrl] = useState<string>(currentUser?.pictureUrl);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState<string>(currentUser?.coverPhotoUrl);
  const [coverPhotoModalOpen, setCoverPhotoModalOpen] = useState<boolean>(false);
  const [updateProfile, updateProfileMutation] = useMutation(gql`
    mutation ($input: UpdateProfileInput!) {
      updateProfile(input: $input) {
        viewer {
          id
          currentUser {
            id
            bio
            shortBio
            email
            firstName
            lastName
            isPasswordSet
            phone
            pictureUrl
            coverPhotoUrl
            location
            locationCity
            locationState
            locationCountryKey
            locationLatitude
            locationLongitude
            profileVisibilityKey
          }
        }
      }
    }
  `);
  const form = useForm<UserProfileFormFields>({
    defaultValues: {
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      email: currentUser?.email,
      phone: currentUser?.phone,
      title: currentUser?.title,
      shortBio: currentUser?.shortBio,
      bio: currentUser?.bio,
      pictureUrl: currentUser?.pictureUrl,
      coverPhotoUrl: currentUser?.coverPhotoUrl,
      profileVisibilityKey: currentUser?.profileVisibilityKey
    }
  });
  const [locationPlace, setLocationPlace] = useState<LocationInputPlace>(currentUser?.location ? {
    placeId: 'default',
    title: currentUser.location
  } : null);
  const handleSubmit = useCallback(values => processSubmit({
    values,
    props,
    setFormErrorMessage,
    action: async values_0 => {
      const result = await updateProfile({
        variables: {
          input: {
            ...(values_0 as UpdateProfileInput),
            ...(locationPlace && locationPlace.placeId != 'default' ? {
              location: locationPlace.title,
              locationCity: locationPlace.city,
              locationState: locationPlace.state,
              locationCountryKey: locationPlace.countryISO || locationPlace.country,
              locationPlaceId: locationPlace.placeId,
              locationLatitude: locationPlace.latitude,
              locationLongitude: locationPlace.longitude
            } : {})
          }
        }
      });
      return result;
    },
    afterSuccess: async (values_1, result_0) => {
      setFormSuccessMessage('Profile Saved');
      setTimeout(() => setFormSuccessMessage(null), 3000);
      await refetchCurrentUser();
    }
  }), [locationPlace?.placeId]);
  useEffect(() => {
    if (props.submitTrigger) {
      form.handleSubmit(handleSubmit)();
    }
  }, [props.submitTrigger]);
  useEffect(() => {
    if (currentUser?.pictureUrl) {
      setPictureUrl(currentUser.pictureUrl);
    }
  }, [currentUser?.pictureUrl]);
  useEffect(() => {
    if (currentUser?.coverPhotoUrl) {
      setCoverPhotoUrl(currentUser.coverPhotoUrl);
    }
  }, [currentUser?.coverPhotoUrl]);
  return <FormContainer {...props.containerProps}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <VStack align={'stretch'} gap={4} justify={'stretch'}>
          {props.title && <Heading>{props.title}</Heading>}

          {formErrorMessage && <Alert.Root borderRadius={'md'} status={'error'}>
              <Alert.Indicator />
              <Box marginLeft={'1em'}>{formErrorMessage}</Box>
            </Alert.Root>}

          <SimpleGrid columns={getBreakpointValue([1, 1, 2])} gap={4}>
            <Field.Root invalid={!!form.formState.errors.firstName}>
              <Field.Label>First Name</Field.Label>
              <Input type={'text'} {...form.register('firstName', {
              required: 'Required'
            })} />
              <Field.ErrorText>{form.formState.errors.firstName?.message}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!form.formState.errors.lastName}>
              <Field.Label>Last Name</Field.Label>
              <Input type={'text'} {...form.register('lastName', {
              required: 'Required'
            })} />
              <Field.ErrorText>{form.formState.errors.lastName?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>

          <SimpleGrid columns={getBreakpointValue([1, 1, 2])} gap={'1.5em'}>
            <Field.Root invalid={!!form.formState.errors.email}>
              <Field.Label>Email</Field.Label>
              <Input type={'text'} {...form.register('email', {
              required: 'Required'
            })} />
              <Field.ErrorText>{form.formState.errors.email?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={!!form.formState.errors.phone}>
              <Field.Label>Phone</Field.Label>
              <Input type={'text'} {...form.register('phone', {})} />
              <Field.ErrorText>{form.formState.errors.phone?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>

          <Field.Root>
            <Field.Label>Location</Field.Label>
            {currentUser && <LocationInput placeTypes={['(cities)']} defaultValue={currentUser?.location ? {
            placeId: 'default',
            title: currentUser.location
          } : null} onChange={place => {
            setLocationPlace(place);
          }} />}
          </Field.Root>

          <Field.Root invalid={!!form.formState.errors.title}>
            <Field.Label>Title / Position</Field.Label>
            <Input placeholder={'Founder @ Your Company'} type={'text'} {...form.register('title', {})} />
            <Field.ErrorText>{form.formState.errors.title?.message}</Field.ErrorText>
          </Field.Root>

          <Field.Root invalid={!!form.formState.errors.shortBio}>
            <Field.Label>Short Bio</Field.Label>
            <Input placeholder={'A few words to describe yourself.'} type={'text'} {...form.register('shortBio', {})} />
            <Field.ErrorText>{form.formState.errors.shortBio?.message}</Field.ErrorText>
          </Field.Root>

          <Field.Root invalid={!!form.formState.errors.bio}>
            <Field.Label>Full Bio</Field.Label>

            <AutoResizeTextarea minHeight={'100px'} placeholder={'Tell us a bit more about yourself and experiences'} {...form.register('bio', {})} />
            <Field.ErrorText>{form.formState.errors.bio?.message}</Field.ErrorText>
          </Field.Root>

          <Field.Root>
            <Field.Label>Profile Photos</Field.Label>
            <SimpleGrid columns={getBreakpointValue([1, 1, 2])} gap={4} width={'full'}>
              <Box bgColor={'containerSecondary'} border={'1px solid'} borderColor={'actionPrimaryAlpha.200'} borderRadius={'md'} height={'full'} padding={'1em 1.5em'}>
                <Stack align={'center'} gap={4} height={'full'} justify={'center'}>
                  <Box textAlign={'center'}>
                    <Avatar.Root size={'2xl'}>
                      <Avatar.Fallback name={[form.getValues().firstName, form.getValues().lastName].join(' ').trim() || form.getValues().email} />
                      <Avatar.Image src={pictureUrl} />
                    </Avatar.Root>
                  </Box>
                  <Box flexGrow={1} width={'full'}>
                    <Stack align={'center'} direction={getBreakpointValue(['column', 'row'])} justify={'center'} width={'full'}>
                      <Box>
                        <UploadButton afterError={err => alert(`Upload failed: ${err.toString()}`)} allowedExtensions={['jpeg', 'jpg', 'png']} buttonProps={{
                        size: 'sm'
                      }} maxSizeInBytes={10000000} // 10 MB
                      uploadUrl={scoBackendUrl('/assets/upload-image')} afterSuccess={url => {
                        form.setValue('pictureUrl', url);
                        setPictureUrl(url);
                      }} storedFile={{
                        attachmentType: 'User',
                        attachmentId: currentUser?.userId,
                        contextKey: 'profile-picture'
                      }}>
                          Upload a Profile Photo
                        </UploadButton>
                      </Box>
                      <Box>or</Box>
                      <Button size={'sm'} variant={'outline'} onClick={() => {
                      form.setValue('pictureUrl', null);
                      setPictureUrl(undefined);
                    }}>
                        Remove
                      </Button>
                    </Stack>
                    <Text color={'colorPalette.fg/60'} fontSize={'0.9em'} marginTop={'0.5em'} textAlign={'center'}>
                      A square JPEG or PNG under 10MB is best.
                    </Text>
                  </Box>
                </Stack>
              </Box>

              <Box bgColor={'containerSecondary'} border={'1px solid'} borderColor={'actionPrimaryAlpha.200'} borderRadius={'md'} height={'full'} padding={'1em 1.5em'}>
                <Stack align={'center'} gap={4} height={'full'} justify={'center'}>
                  <Box aspectRatio={'16/6'} backgroundPosition={`center ${currentUser?.coverPhotoYInPercent ?? 50}%`} backgroundSize={'cover'} bgColor={'blackAlpha.300'} bgImage={`url(${coverPhotoUrl || DEFAULT_COVER_PHOTO_URL})`} borderRadius={'md'} boxShadow={'inset 0 -8px 12px -6px rgba(0, 0, 0, 0.35)'} width={'full'} />
                  <Button size={'sm'} variant={'outlineGhost'} onClick={() => setCoverPhotoModalOpen(true)}>
                    <LuImage />
                    Edit Cover Photo
                  </Button>
                </Stack>
              </Box>
            </SimpleGrid>

            <UserCoverPhotoSelectorModal isOpen={coverPhotoModalOpen} onClose={async () => {
            setCoverPhotoModalOpen(false);
            await refetchCurrentUser();
          }} />
          </Field.Root>

          {props.showAdditionalFields && <Field.Root invalid={!!form.formState.errors.profileVisibilityKey}>
              <Field.Label>Profile Visibility</Field.Label>

              <NativeSelect.Root>
                <NativeSelect.Field {...form.register('profileVisibilityKey')}>
                  {profileVisibilities.map(item => <option key={item.key} value={item.key}>
                      {item.title}
                      {': '}
                      {item.description}
                    </option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>

              <Field.ErrorText>{form.formState.errors.profileVisibilityKey?.message}</Field.ErrorText>
              <Text color={'colorPalette.fg/50'} fontSize={'12px'} marginY={'0.5em'}>
                {'Your Profile is at:'}{' '}
                <Link asChild variant={'underline'}>
                  <NextLink href={`/members/${currentUser?.userId}`}>
                    {startupsUrl(`/members/${currentUser?.userId}`)}
                  </NextLink>
                </Link>
              </Text>
            </Field.Root>}

          <Button display={props.submitControlled ? 'none' : null} loading={form.formState.isSubmitting} type={'submit'} variant={'cta'}>
            {formSuccessMessage ? <>
                <CheckCircleIcon marginRight={'1em'} /> {formSuccessMessage}
              </> : 'Save Profile'}
          </Button>
        </VStack>
      </form>
    </FormContainer>;
};